/**
 * 统一API客户端 - 管理所有/apps接口
 * 方便后期修改接口前缀、统一错误处理等
 */

class ApiClient {
  constructor() {
    // 基础配置
    this.baseUrl = '/apps/account';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // 接口端点配置
    this.endpoints = {
      // 购物车相关
      cartV2: '/cart-v2',
      cartDiscount: '/cart-discount',
      cartId: '/cart-id',

      // 用户相关
      bootstrap: '/bootstrap',
      customer: '/customer',
      addresses: '/addresses',
      orders: '/orders',
      wishlist: '/wishlist',
      coupons: '/coupons',
    };
  }

  /**
   * 通用请求方法
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      method: 'GET',
      headers: { ...this.defaultHeaders, ...options.headers },
      credentials: 'same-origin',
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * 购物车相关接口
   */

  // 获取购物车信息
  async getCart(cartId) {
    return this.request(
      `${this.endpoints.cartV2}?cartId=${encodeURIComponent(cartId)}`,
    );
  }

  // 购物车操作
  async cartAction(action, data) {
    return this.request(this.endpoints.cartV2, {
      method: 'POST',
      body: JSON.stringify({ action, ...data }),
    });
  }

  // 购物车V2接口
  async cartV2Action(action, data) {
    return this.request(this.endpoints.cartV2, {
      method: 'POST',
      body: JSON.stringify({ action, ...data }),
    });
  }

  // 获取购物车V2信息
  async getCartV2(cartId) {
    return this.request(
      `${this.endpoints.cartV2}?cartId=${encodeURIComponent(cartId)}`,
    );
  }

  // 创建购物车
  async createCart() {
    return this.cartV2Action('create');
  }

  // 添加商品到购物车
  async addToCart(cartId, variantId, quantity = 1, properties = {}) {
    return this.cartV2Action('add', {
      cartId,
      variantId,
      quantity,
      properties,
    });
  }

  // 更新商品数量
  async updateQuantity(cartId, lineId, quantity) {
    return this.cartV2Action('update_quantity', {
      cartId,
      lineId,
      quantity,
    });
  }

  // 移除商品
  async removeItem(cartId, lineId) {
    return this.cartV2Action('remove_item', {
      cartId,
      lineId,
    });
  }

  // 添加服务
  async addService(cartId, baseProductId, serviceProductId, quantity = 1) {
    return this.cartV2Action('add_service', {
      cartId,
      baseProductId,
      serviceProductId,
      quantity,
    });
  }

  // 移除服务
  async removeService(cartId, serviceLineId) {
    return this.cartV2Action('remove_service', {
      cartId,
      serviceLineId,
    });
  }

  // 更新服务数量
  async updateServiceQuantity(cartId, serviceLineId, quantity) {
    return this.cartV2Action('update_service_quantity', {
      cartId,
      serviceLineId,
      quantity,
    });
  }

  // 选择结账商品
  async selectedCheckout(
    cartId,
    selections,
    includeBoundServices = true,
    applyCartDiscounts = true,
  ) {
    return this.cartV2Action('selected_checkout', {
      cartId,
      selections,
      includeBoundServices,
      applyCartDiscounts,
    });
  }

  // 购物车折扣码操作
  async cartDiscountAction(action, cartId, code) {
    return this.request(this.endpoints.cartDiscount, {
      method: 'POST',
      body: JSON.stringify({ action, cartId, code }),
    });
  }

  // 添加折扣码
  async addDiscountCode(cartId, code) {
    return this.cartDiscountAction('add', cartId, code);
  }

  // 移除折扣码
  async removeDiscountCode(cartId, code) {
    return this.cartDiscountAction('remove', cartId, code);
  }

  // 获取购物车ID
  async getCartId() {
    return this.request(this.endpoints.cartId);
  }

  // 获取购物车关怀计划摘要
  async getCareSummary(cartId) {
    return this.cartAction('care_summary', { cartId });
  }

  // 移除商品（包含服务）
  async removeProduct(cartId, variantId) {
    return this.cartAction('product_remove', { cartId, variantId });
  }

  /**
   * 用户相关接口
   */

  // 获取用户信息
  async getCustomer() {
    return this.request(this.endpoints.customer);
  }

  // 获取用户地址
  async getAddresses() {
    return this.request(this.endpoints.addresses);
  }

  // 更新用户地址
  async updateAddress(addressData) {
    return this.request(this.endpoints.addresses, {
      method: 'POST',
      body: JSON.stringify(addressData),
    });
  }

  // 获取用户订单
  async getOrders() {
    return this.request(this.endpoints.orders);
  }

  // 获取愿望清单
  async getWishlist() {
    return this.request(this.endpoints.wishlist);
  }

  // 愿望清单操作
  async wishlistAction(op, data) {
    return this.request(this.endpoints.wishlist, {
      method: 'POST',
      body: JSON.stringify({ op, ...data }),
    });
  }

  // 添加商品到愿望清单
  async addToWishlist(productId, variantId) {
    return this.wishlistAction('add', { productId, variantId });
  }

  // 从愿望清单移除商品
  async removeFromWishlist(productId, variantId) {
    return this.wishlistAction('remove', { productId, variantId });
  }

  // 获取优惠券列表
  async getCoupons() {
    return this.request(this.endpoints.coupons, {
      method: 'POST',
      body: JSON.stringify({ couponOp: 'list' }),
    });
  }

  // 使用优惠券
  async useCoupon(couponCode) {
    return this.request(this.endpoints.coupons, {
      method: 'POST',
      body: JSON.stringify({ couponOp: 'use', couponCode }),
    });
  }

  // 引导接口
  async bootstrap(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`${this.endpoints.bootstrap}?${queryString}`, {
      method: 'POST',
    });
  }

  /**
   * 工具方法
   */

  // 设置基础URL（用于切换环境）
  setBaseUrl(url) {
    this.baseUrl = url;
  }

  // 获取完整URL
  getFullUrl(endpoint) {
    return `${this.baseUrl}${endpoint}`;
  }

  // 检查响应状态
  isSuccess(response) {
    return response && response.ok === true;
  }

  // 获取错误信息
  getErrorMessage(response) {
    if (response && response.error) {
      return response.error;
    }
    return 'Unknown error occurred';
  }
}

// 创建全局实例
const apiClient = new ApiClient();

// 导出实例和类
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { ApiClient, apiClient };
} else if (typeof window !== 'undefined') {
  window.apiClient = apiClient;
  window.ApiClient = ApiClient;
}
